import { TFunction } from 'react-i18next';
import {
  ChartOptions,
  ColorType,
  CrosshairMode,
  DeepPartial,
  HistogramData,
  LineData,
  LineStyle,
  TickMarkType,
  Time,
  UTCTimestamp,
  WhitespaceData
} from 'lightweight-charts';

import {
  BACKGROUND_DARK_THEME_COLOR,
  BACKGROUND_LIGHT_THEME_COLOR,
  BORDER_DARK_THEME_COLOR,
  BORDER_LIGHT_THEME_COLOR,
  ChartSelections,
  INTERVAL_1_DAY,
  INTERVAL_2_HOURS,
  INTERVAL_5_MIN,
  INTERVAL_15_SEC,
  MarketGraphChartDataGroupingPeriods,
  ONE_HOUR_IN_MILLISECONDS,
  TEXT_COLOR
} from 'constants/marketGraph';
import { MarketGraphChartSelection, MarketGraphPoint } from 'types/marketGraph';
import { getMonthLabelTranslationKey } from 'utils/date';

import { addZero } from './session';

const calculateWeightedAverage = (data: MarketGraphPoint[], intervalStart: number) => {
  if (data.length === 1) {
    return {
      timestamp: intervalStart as UTCTimestamp,
      value: data[0].value,
      volume: data[0].volume
    };
  }

  let totalVolume = 0;
  let weightedOddsSum = 0;

  for (const point of data) {
    const volume = point.volume ?? 0;
    const odds = point.value ?? 0;

    totalVolume += volume;
    weightedOddsSum += (odds - 1) * volume;
  }

  const avgOdds = totalVolume === 0 ? undefined : weightedOddsSum / totalVolume + 1;

  return {
    timestamp: intervalStart as UTCTimestamp,
    value: avgOdds,
    volume: totalVolume
  };
};

export const groupChartDataByInterval = ({ data, interval }: { data: MarketGraphPoint[]; interval: number }) => {
  const normalizedData: MarketGraphPoint[] = [];

  if (!data || data.length === 0 || interval <= 0) {
    return normalizedData;
  }

  let currentIntervalStart = Math.floor(data[0].timestamp / interval) * interval;
  let currentIntervalEnd = currentIntervalStart + interval;
  let dataIndex = 0; // Index to iterate through the sorted data
  let normalizedDataIndex = 0;

  while (currentIntervalEnd <= data[data.length - 1].timestamp + interval) {
    // Iterate until past the last data point + one interval to fill gaps
    const currentIntervalData = [];

    //Collect data points within the current interval
    while (
      dataIndex < data.length &&
      data[dataIndex].timestamp >= currentIntervalStart &&
      data[dataIndex].timestamp < currentIntervalEnd
    ) {
      currentIntervalData.push(data[dataIndex]);
      dataIndex++;
    }

    if (currentIntervalData.length > 0) {
      normalizedData.push(calculateWeightedAverage(currentIntervalData, currentIntervalStart));
    } else {
      const prevData = normalizedDataIndex > 0 ? normalizedData[normalizedDataIndex - 1] : undefined;

      normalizedData.push({
        timestamp: currentIntervalStart,
        value: prevData?.value
      });
    }

    currentIntervalStart = currentIntervalEnd;
    currentIntervalEnd += interval;
    normalizedDataIndex++;
  }

  return normalizedData;
};

export const tickTimeFormatter = ({
  time,
  americanDateFormatEnabled,
  // locale, not used, but can be used in future, get it from lightweight-charts by default
  tickMarkType,
  t
}: {
  time: Time;
  tickMarkType: TickMarkType;
  locale: string;
  americanDateFormatEnabled: boolean;
  t: TFunction;
}) => {
  const date = new Date((time as number) * 1000);

  if (tickMarkType === 0) {
    return date.getFullYear();
  } else if (tickMarkType === 1) {
    return t(getMonthLabelTranslationKey(date.getMonth(), true));
  }
  if (tickMarkType === 2) {
    const day = date.getDate();
    const month = t(getMonthLabelTranslationKey(date.getMonth(), true));

    return `${americanDateFormatEnabled ? month : day} ${americanDateFormatEnabled ? day : month}`;
  } else if (tickMarkType === 3 || tickMarkType === 4) {
    const hours = date.getHours();
    const displayedHours = `${addZero(hours)}${hours}`;
    const minutes = date.getMinutes();
    const displayedMinutes = `${addZero(minutes)}${minutes}`;
    let displayedSeconds: string | undefined;

    if (tickMarkType === 4) {
      const seconds = date.getSeconds();
      displayedSeconds = `${addZero(seconds)}${seconds}`;
    }

    return `${displayedHours}:${displayedMinutes}${displayedSeconds ? `:${displayedSeconds}` : ''}`;
  }
};

export const convertOddsChance = (value: number) => {
  return (1 / value) * 100;
};

export const getLineChartDataValue = ({
  value,
  selection
}: {
  value?: number;
  selection: MarketGraphChartSelection;
}) => {
  if (selection === ChartSelections.odds || !value) {
    return value;
  }

  return convertOddsChance(value);
};

// Convert value to seconds
export const getChartTimeValue = (timestamp: number) => (timestamp / 1000) as UTCTimestamp;

export const getChartInterval = ({
  startTimestamp,
  endTimestamp
}: {
  startTimestamp: number;
  endTimestamp: number;
}) => {
  const diff = endTimestamp - startTimestamp;

  if (diff / INTERVAL_1_DAY > 31) {
    return { interval: INTERVAL_1_DAY, period: MarketGraphChartDataGroupingPeriods.day1 };
  }

  if (diff / INTERVAL_1_DAY > 1 && diff / INTERVAL_1_DAY <= 31) {
    return { interval: INTERVAL_2_HOURS, period: MarketGraphChartDataGroupingPeriods.hour2 };
  }

  if (diff / ONE_HOUR_IN_MILLISECONDS > 1 && diff / INTERVAL_1_DAY <= 1) {
    return { interval: INTERVAL_5_MIN, period: MarketGraphChartDataGroupingPeriods.min5 };
  }

  return { interval: INTERVAL_15_SEC, period: MarketGraphChartDataGroupingPeriods.sec15 };
};

export const getLineAndHistogramData = ({
  chartData,
  chartSelection,
  interval
}: {
  chartData: MarketGraphPoint[];
  chartSelection: MarketGraphChartSelection;
  interval: number;
}) => {
  const lineData: (WhitespaceData | LineData)[] = [];
  const histogramData: (WhitespaceData | HistogramData)[] = [];

  const groupedChartData = groupChartDataByInterval({ data: chartData, interval });

  for (const item of groupedChartData) {
    const time = getChartTimeValue(item.timestamp);
    const oddsValue = getLineChartDataValue({
      value: item.value,
      selection: chartSelection
    });

    lineData.push({
      time,
      value: oddsValue
    });
    histogramData.push({ time, value: item.volume });
  }

  return { lineData, histogramData };
};

export const getChartOptions = ({
  isDarkTheme,
  americanDateFormatEnabled,
  t,
  width,
  isSimplifiedView
}: {
  isDarkTheme: boolean;
  width: number;
  isSimplifiedView: boolean;
  americanDateFormatEnabled: boolean;
  t: TFunction;
}): DeepPartial<ChartOptions> => {
  return {
    layout: {
      background: {
        type: ColorType.Solid,
        color: isDarkTheme ? BACKGROUND_DARK_THEME_COLOR : BACKGROUND_LIGHT_THEME_COLOR
      },
      textColor: TEXT_COLOR,
      fontSize: 12,
      attributionLogo: false,
      panes: {
        enableResize: false
      }
    },
    width,
    rightPriceScale: {
      visible: !isSimplifiedView, // Hide volume Y-scale if simplified view ia applied
      borderVisible: false,
      entireTextOnly: true
    },
    leftPriceScale: {
      visible: true,
      borderVisible: false,
      entireTextOnly: true
    },
    grid: {
      vertLines: { visible: false }, // Hide vertical grid lines
      horzLines: { color: isDarkTheme ? BORDER_DARK_THEME_COLOR : BORDER_LIGHT_THEME_COLOR, style: LineStyle.Dashed } // Dashed horizontal lines
    },
    crosshair: {
      mode: CrosshairMode.Hidden
    },
    handleScale: {
      axisPressedMouseMove: {
        price: false
      }
    },
    timeScale: {
      timeVisible: true,
      secondsVisible: true,
      fixRightEdge: true,
      fixLeftEdge: true,
      ticksVisible: true,
      borderColor: isDarkTheme ? BORDER_DARK_THEME_COLOR : BORDER_LIGHT_THEME_COLOR,
      tickMarkFormatter: (time: Time, tickMarkType: TickMarkType, locale: string) =>
        tickTimeFormatter({ time, tickMarkType, locale, americanDateFormatEnabled, t }),
      rightBarStaysOnScroll: false,
      allowShiftVisibleRangeOnWhitespaceReplacement: true,
      allowBoldLabels: false
    }
  };
};

export const convertLineChartDataForSelection = ({ data }: { data: (WhitespaceData | LineData)[] }) => {
  return data.map(item => {
    if ('value' in item) {
      return {
        ...item,
        value: convertOddsChance(item.value)
      };
    }

    return item;
  });
};

export const concatExistAndPrevChartData = ({
  prevLineChartData,
  newLineChartData,
  prevHistogramChartData,
  newHistogramChartData
}: {
  prevLineChartData: (WhitespaceData | LineData)[];
  newLineChartData: (WhitespaceData | LineData)[];
  prevHistogramChartData: (WhitespaceData | HistogramData)[];
  newHistogramChartData: (WhitespaceData | HistogramData)[];
}) => {
  if (!prevLineChartData.length) {
    return { lineData: newLineChartData, histogramData: newHistogramChartData };
  }

  if (!newLineChartData.length) {
    return { lineData: prevLineChartData, histogramData: prevHistogramChartData };
  }

  let lineDataResult: (WhitespaceData | LineData)[] = [];
  let histogramDataResult: (WhitespaceData | HistogramData)[] = [];

  const lastPointFromPrevLineData = prevLineChartData[prevLineChartData.length - 1];
  const [firstPointFromNewLineData, ...restNewLineData] = newLineChartData;

  // timestamps always should be unique in data array for LineSeries and HistogramSeries, so if timestamp from last the
  // point of grouped data is equal to timestamp from the first point of new grouped data need to combine them into 1 point
  if (lastPointFromPrevLineData.time === firstPointFromNewLineData.time) {
    const lastPointFromPrevHistogramData = prevHistogramChartData[prevHistogramChartData.length - 1];
    const [firstPointFromNewHistogramData, ...restNewHistogramData] = newHistogramChartData;

    if (
      'value' in firstPointFromNewLineData &&
      'value' in lastPointFromPrevLineData &&
      'value' in firstPointFromNewHistogramData &&
      'value' in lastPointFromPrevHistogramData
    ) {
      const totalVolume = lastPointFromPrevHistogramData.value + firstPointFromNewHistogramData.value;
      const weightedOddsSum =
        lastPointFromPrevLineData.value + (firstPointFromNewLineData.value - 1) * firstPointFromNewHistogramData.value;
      const avgOdds = totalVolume === 0 ? undefined : weightedOddsSum / totalVolume + 1;

      lineDataResult = [
        ...prevLineChartData.slice(0, -1),
        {
          ...lastPointFromPrevHistogramData,
          value: avgOdds
        },
        ...restNewLineData
      ];

      histogramDataResult = [
        ...prevHistogramChartData.slice(0, -1),
        {
          ...lastPointFromPrevHistogramData,
          value: totalVolume
        },
        ...restNewHistogramData
      ];
    }
  } else {
    lineDataResult = prevLineChartData.concat(newLineChartData);
    histogramDataResult = prevHistogramChartData.concat(newHistogramChartData);
  }

  return { lineData: lineDataResult, histogramData: histogramDataResult };
};
